import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, Brush, Library as LibraryIcon, Users, Cpu, DollarSign,
  Upload, Video, Loader2, Send, X, Wand2, Clock, ArrowRight, Film,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import VideoThumbnail from '../components/VideoThumbnail';
import { formatCredits } from '../utils/credits';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV_ITEMS = [
  { id: 'generate', label: 'Generate', icon: Sparkles, path: '/generate' },
  { id: 'canvas', label: 'Canvas', icon: Brush, path: '/canvas' },
  { id: 'image-to-video', label: 'Image → Video', icon: Film, path: '/image-to-video' },
  { id: 'library', label: 'Library', icon: LibraryIcon, path: '/library' },
  { id: 'community', label: 'Community', icon: Users, path: '/community' },
];

const ASPECT_RATIOS = [
  { id: '16:9', label: 'Landscape' },
  { id: '1:1',  label: 'Square' },
  { id: '9:16', label: 'Portrait' },
];
const DURATIONS = [
  { value: 4,  label: '4 sec',  cost: 25 },
  { value: 8,  label: '8 sec',  cost: 40 },
  { value: 12, label: '12 sec', cost: 55 },
];

const MOTION_PRESETS = [
  { id: 'cinematic_zoom',  label: 'Cinematic zoom-in',     prompt: 'slow cinematic zoom in, dramatic depth of field, anamorphic motion' },
  { id: 'parallax_pan',    label: 'Parallax pan',          prompt: 'smooth horizontal parallax pan, foreground and background moving at different speeds' },
  { id: 'gentle_drift',    label: 'Gentle drift',          prompt: 'gentle floating drift, subtle ambient motion, dreamy atmosphere' },
  { id: 'wind_motion',     label: 'Wind & movement',       prompt: 'wind blowing through hair and clothes, leaves swaying, natural environmental motion' },
  { id: 'orbit',           label: 'Orbit / dolly arc',     prompt: 'dolly arc around subject, smooth camera orbit, parallax depth' },
  { id: 'reveal',          label: 'Slow reveal',           prompt: 'slow reveal pull-back shot, gradually showing more of the scene' },
  { id: 'lights_glow',     label: 'Lights glow & flicker', prompt: 'lights glowing and softly flickering, neon reflections moving, atmospheric haze' },
  { id: 'liquid',          label: 'Liquid motion',         prompt: 'liquid water or fluid moving in slow motion, ripples and reflections' },
];

export default function ImageToVideo() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fileInputRef = useRef(null);

  const [imagePath, setImagePath] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [motionPrompt, setMotionPrompt] = useState('');
  const [selectedMotion, setSelectedMotion] = useState(null);
  const [aspect, setAspect] = useState('16:9');
  const [duration, setDuration] = useState(4);
  const [generating, setGenerating] = useState(false);
  const [activeJob, setActiveJob] = useState(null);
  const [recent, setRecent] = useState([]);
  const [autoAnimatePrompt, setAutoAnimatePrompt] = useState(null);

  // Preload source image from ?source=<storage_path> and open auto-animate confirm modal
  useEffect(() => {
    const src = searchParams.get('source');
    if (src && !imagePath) {
      setImagePath(src);
      setImagePreview(`${API}/files/${src}`);
      const drift = MOTION_PRESETS.find((m) => m.id === 'gentle_drift');
      if (drift) {
        setSelectedMotion(drift);
        setMotionPrompt(drift.prompt);
      }
      setAutoAnimatePrompt(drift);
      const cleared = new URLSearchParams(searchParams);
      cleared.delete('source');
      setSearchParams(cleared, { replace: true });
    }
  }, []);

  // Fetch recent i2v jobs
  const fetchRecent = async () => {
    try {
      const r = await axios.get(`${API}/library?kind=video&limit=20`, { withCredentials: true });
      setRecent((r.data || []).filter((j) => j.config?.mode === 'image_to_video'));
    } catch (e) {
      if (e?.response?.status && e.response.status !== 401) {
        console.warn('[i2v] recent jobs fetch failed', e.message);
      }
    }
  };
  useEffect(() => { fetchRecent(); }, []);

  // Poll active job for progress
  useEffect(() => {
    if (!activeJob || activeJob.status === 'completed' || activeJob.status === 'failed') return;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/jobs/${activeJob.job_id}`, { withCredentials: true });
        setActiveJob(r.data);
        if (r.data.status === 'completed') { fetchRecent(); toast.success('Video ready ✨'); }
        if (r.data.status === 'failed')    { toast.error(r.data.error_message || 'Generation failed'); }
      } catch (e) {
        // Transient poll errors are expected during long-running jobs — log only persistent failures
        if (e?.response?.status && e.response.status >= 500) {
          console.warn('[i2v] poll error', e.message);
        }
      }
    }, 4000);
    return () => clearInterval(t);
  }, [activeJob]);

  const handleUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image'); return; }
    if (file.size > 10 * 1024 * 1024)    { toast.error('Max 10 MB'); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API}/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setImagePath(r.data.path);
      setImagePreview(URL.createObjectURL(file));
      toast.success('Image ready to animate');
    } catch (err) {
      toast.error('Upload failed');
    } finally { setUploading(false); }
  };

  const clearImage = () => { setImagePath(null); setImagePreview(null); };

  const pickMotion = (m) => {
    if (selectedMotion?.id === m.id) { setSelectedMotion(null); return; }
    setSelectedMotion(m);
    if (!motionPrompt.trim()) setMotionPrompt(m.prompt);
  };

  const animate = async (override = {}) => {
    if (!imagePath) { toast.error('Upload an image first'); return; }
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/generate/image-to-video`, {
        base_image_path: imagePath,
        motion_prompt: override.motion_prompt ?? (motionPrompt || (selectedMotion?.prompt) || ''),
        model: 'sora-2',
        aspect_ratio: override.aspect_ratio ?? aspect,
        duration: override.duration ?? duration,
      }, { withCredentials: true });
      toast.success(`Animation started — ${override.duration ?? duration}s clip incoming`);
      setActiveJob({ job_id: r.data.job_id, status: 'processing', progress: 10 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start');
    } finally { setGenerating(false); }
  };

  const confirmAutoAnimate = async () => {
    const drift = autoAnimatePrompt;
    setAutoAnimatePrompt(null);
    await animate({ motion_prompt: drift?.prompt, duration: 4 });
  };

  const dur = DURATIONS.find((d) => d.value === duration);

  return (
    <div className="min-h-screen bg-[#0A0A0F] text-white flex" data-testid="image-to-video-page">
      {/* Auto-animate confirm modal */}
      {autoAnimatePrompt && imagePreview && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-6" data-testid="auto-animate-modal">
          <div className="relative w-full max-w-md rounded-3xl border border-[#E040A0]/30 bg-gradient-to-br from-[#1a0a1f] via-[#0A0A0F] to-[#1a0a1f] p-6 shadow-[0_20px_80px_rgba(224,64,160,0.3)]">
            <div className="aspect-video rounded-xl overflow-hidden mb-5 border border-white/[0.08] bg-black">
              <img src={imagePreview} alt="preview" className="w-full h-full object-cover" />
            </div>
            <div className="text-[10px] font-bold tracking-[0.25em] text-[#E040A0] uppercase mb-2">One-click animate</div>
            <h3 className="font-display text-2xl font-black text-white tracking-tight mb-2">Bring this to life?</h3>
            <p className="text-sm text-white/60 leading-relaxed mb-5">
              We&apos;ll animate it with a <span className="text-white font-semibold">{autoAnimatePrompt.label.toLowerCase()}</span> — 4 second clip via Sora 2.
              You can change the motion below afterwards.
            </p>
            <div className="flex items-center justify-between mb-5 p-3 rounded-xl bg-white/[0.04] border border-white/[0.06]">
              <div className="flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-white/40" />
                <span className="text-xs text-white/60">~3 minutes</span>
              </div>
              <div className="text-xs">
                <span className="text-[#FFD93D] font-bold">25 credits</span>
                <span className="text-white/40 ml-1.5">/ {formatCredits(user)} avail</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setAutoAnimatePrompt(null)} data-testid="auto-animate-cancel"
                className="flex-1 py-3 rounded-full text-xs font-bold uppercase tracking-[0.15em] text-white/60 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] transition-colors">
                Let me tweak it
              </button>
              <button onClick={confirmAutoAnimate} data-testid="auto-animate-confirm"
                disabled={generating || (user?.credits || 0) < 25}
                className="flex-[1.4] py-3 rounded-full text-xs font-bold uppercase tracking-[0.15em] text-white bg-gradient-to-r from-[#E040A0] to-[#FF6B4A] hover:from-[#FF6B9D] hover:to-[#FF8E72] shadow-[0_8px_24px_rgba(224,64,160,0.4)] disabled:opacity-40 disabled:cursor-not-allowed transition-shadow flex items-center justify-center gap-2">
                {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Wand2 className="w-3.5 h-3.5" /> Animate now · 25 cr</>}
              </button>
            </div>
          </div>
        </div>
      )}

      <Sidebar location={location} user={user} />

      <main className="flex-1 overflow-y-auto">
        <header className="px-6 lg:px-10 py-8 border-b border-white/[0.05] flex items-center justify-between">
          <div>
            <p className="text-[10px] font-semibold tracking-[0.25em] text-[#E040A0] uppercase mb-2">Image → Video</p>
            <h1 className="font-display text-3xl font-bold text-white" data-testid="i2v-heading">Bring any still to life</h1>
            <p className="text-sm text-white/50 mt-1">Upload an image, describe the motion, get a 4–12 second animated clip. Powered by Sora 2.</p>
          </div>
          <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.08]">
            <Cpu className="w-3 h-3 text-[#FFD93D]" />
            <span className="text-[10px] font-bold tracking-[0.2em] text-white/60 uppercase">Sora 2 · server-side · always available</span>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 p-6 lg:p-10">

          {/* LEFT — Upload + preview */}
          <div className="space-y-5">
            <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-6">
              <p className="text-[10px] font-bold tracking-[0.2em] text-white/40 uppercase mb-3">1. Source image</p>
              {imagePreview ? (
                <div className="relative rounded-xl overflow-hidden border border-white/[0.06]" data-testid="i2v-preview">
                  <img src={imagePreview} alt="source" className="w-full max-h-[420px] object-contain bg-black" />
                  <button onClick={clearImage} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/70 hover:bg-black/90 flex items-center justify-center" data-testid="i2v-clear-image">
                    <X className="w-4 h-4 text-white" />
                  </button>
                </div>
              ) : (
                <label className="block cursor-pointer" data-testid="i2v-upload-label">
                  <div className="border-2 border-dashed border-white/[0.08] rounded-xl p-12 text-center hover:border-[#E040A0]/40 hover:bg-white/[0.02] transition-colors">
                    <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload(e.target.files[0])} data-testid="i2v-file-input" />
                    {uploading ? (
                      <Loader2 className="w-8 h-8 text-white/40 animate-spin mx-auto" />
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-white/40 mx-auto mb-3" />
                        <div className="text-sm text-white/70 font-semibold">Drop or click to upload</div>
                        <div className="text-xs text-white/40 mt-1">PNG · JPG · WebP — max 10 MB</div>
                      </>
                    )}
                  </div>
                </label>
              )}
            </div>

            {/* Motion description */}
            <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-6">
              <p className="text-[10px] font-bold tracking-[0.2em] text-white/40 uppercase mb-3">2. How should it move?</p>
              <div className="flex flex-wrap gap-1.5 mb-4">
                {MOTION_PRESETS.map((m) => (
                  <button key={m.id} onClick={() => pickMotion(m)}
                    data-testid={`motion-${m.id}`}
                    className={`px-3 py-1.5 rounded-full text-[10px] font-bold tracking-wider uppercase transition-colors ${selectedMotion?.id === m.id ? 'bg-[#E040A0] text-white' : 'bg-white/[0.05] text-white/60 hover:bg-white/[0.1]'}`}>
                    {m.label}
                  </button>
                ))}
              </div>
              <textarea
                value={motionPrompt}
                onChange={(e) => setMotionPrompt(e.target.value)}
                placeholder="e.g. slow cinematic zoom-in, hair gently blowing in the wind, neon reflections shimmering on wet asphalt..."
                className="w-full h-24 bg-black/30 border border-white/[0.06] rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#E040A0]/50 resize-none"
                data-testid="i2v-motion-input"
              />
              <p className="text-[10px] text-white/40 mt-2">Tip: be specific — camera moves, environment changes, subject actions. Leave blank for natural cinematic motion.</p>
            </div>

            {/* Active job progress */}
            {activeJob && (
              <div className="rounded-2xl border border-[#E040A0]/30 bg-gradient-to-br from-[#E040A0]/[0.08] to-transparent p-6" data-testid="i2v-active-job">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {activeJob.status === 'processing' ? <Loader2 className="w-4 h-4 text-[#E040A0] animate-spin" /> : <Video className="w-4 h-4 text-[#34B76F]" />}
                    <span className="text-xs font-bold tracking-wider uppercase text-white">{activeJob.status === 'completed' ? 'Done' : activeJob.status === 'failed' ? 'Failed' : 'Animating'}</span>
                  </div>
                  <span className="text-xs text-white/40 flex items-center gap-1"><Clock className="w-3 h-3" /> ~3 min</span>
                </div>
                {activeJob.status === 'processing' && (
                  <div className="h-1.5 rounded-full bg-white/[0.08] overflow-hidden mb-2">
                    <div className="h-full bg-gradient-to-r from-[#E040A0] to-[#FF6B4A] transition-all" style={{ width: `${activeJob.progress || 10}%` }} />
                  </div>
                )}
                {activeJob.status === 'completed' && activeJob.output_path && (
                  <div className="mt-3 rounded-xl overflow-hidden bg-black">
                    <video src={`${API}/files/${activeJob.output_path}`} controls autoPlay loop className="w-full max-h-[400px]" />
                  </div>
                )}
              </div>
            )}

            {/* Recent i2v jobs */}
            {recent.length > 0 && (
              <div>
                <p className="text-[10px] font-bold tracking-[0.2em] text-white/40 uppercase mb-3">Your recent animations</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="i2v-recent-grid">
                  {recent.slice(0, 8).map((j) => {
                    const path = j.output_path || j.image_paths?.[0];
                    if (!path) return null;
                    return (
                      <div key={j.job_id} className="rounded-lg overflow-hidden border border-white/[0.06] aspect-video">
                        <VideoThumbnail path={path} title={j.title} className="w-full h-full" />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT — Settings */}
          <aside className="space-y-4">
            <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-5">
              <p className="text-[10px] font-bold tracking-[0.2em] text-white/40 uppercase mb-3">Aspect ratio</p>
              <div className="grid grid-cols-3 gap-1.5">
                {ASPECT_RATIOS.map((a) => (
                  <button key={a.id} onClick={() => setAspect(a.id)}
                    data-testid={`i2v-aspect-${a.id}`}
                    className={`px-2 py-2 rounded-lg text-[11px] font-bold tracking-wider uppercase transition-colors ${aspect === a.id ? 'bg-[#E040A0] text-white' : 'bg-white/[0.04] text-white/60 hover:bg-white/[0.08]'}`}>
                    {a.id}
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-5">
              <p className="text-[10px] font-bold tracking-[0.2em] text-white/40 uppercase mb-3">Duration</p>
              <div className="space-y-1.5">
                {DURATIONS.map((d) => (
                  <button key={d.value} onClick={() => setDuration(d.value)}
                    data-testid={`i2v-duration-${d.value}`}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-colors ${duration === d.value ? 'bg-[#E040A0]/15 text-white border border-[#E040A0]/40' : 'bg-white/[0.04] text-white/60 hover:bg-white/[0.08] border border-transparent'}`}>
                    <span>{d.label}</span>
                    <span className="text-[10px] text-white/40">{d.cost} cr</span>
                  </button>
                ))}
              </div>
            </div>

            <Button
              onClick={animate}
              disabled={!imagePath || generating || (activeJob && activeJob.status === 'processing')}
              className="w-full bg-gradient-to-r from-[#E040A0] to-[#FF6B4A] hover:from-[#FF6B9D] hover:to-[#FF8E72] text-white font-bold uppercase tracking-[0.15em] py-6 rounded-2xl text-xs shadow-[0_8px_24px_rgba(224,64,160,0.3)] disabled:opacity-40 disabled:cursor-not-allowed transition-shadow"
              data-testid="i2v-animate-btn"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> :
                imagePath ? <><Wand2 className="w-4 h-4 mr-2" /> Animate · {dur?.cost} cr</> :
                <><Upload className="w-4 h-4 mr-2" /> Upload an image first</>
              }
            </Button>

            <div className="rounded-2xl bg-gradient-to-br from-[#FFD93D]/[0.08] to-transparent border border-[#FFD93D]/20 p-4">
              <p className="text-[10px] font-bold tracking-[0.2em] text-[#FFD93D] uppercase mb-1">Pro tip</p>
              <p className="text-xs text-white/70 leading-relaxed">
                Got a flagship image you love? Animate it here and post it to the community feed for instant social-ready content.
              </p>
              <Link to="/community" className="inline-flex items-center gap-1 text-[10px] font-bold text-[#FFD93D] hover:text-[#FFE066] mt-2 uppercase tracking-wider">
                See community <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}

function Sidebar({ location, user }) {
  return (
    <aside className="w-[68px] lg:w-[200px] border-r border-white/[0.05] bg-[#08080C] flex flex-col">
      <Link to="/" className="h-16 flex items-center justify-center lg:justify-start lg:px-5 border-b border-white/[0.05]">
        <span className="font-body text-xl font-extrabold tracking-tight text-white">D<span className="text-[#FF6B4A]">2</span><span className="hidden lg:inline">ORT</span></span>
      </Link>
      <nav className="flex-1 py-4 px-2 lg:px-3 space-y-1">
        {NAV_ITEMS.map((it) => {
          const active = location.pathname === it.path;
          return (
            <Link key={it.id} to={it.path}
              data-testid={`sidebar-${it.id}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium transition-colors ${active ? 'bg-[#E040A0]/15 text-[#E040A0]' : 'text-white/60 hover:text-white hover:bg-white/[0.04]'}`}>
              <it.icon className="w-4 h-4 shrink-0" />
              <span className="hidden lg:inline">{it.label}</span>
            </Link>
          );
        })}
        <div className="pt-4 mt-4 border-t border-white/[0.05] space-y-1">
          <Link to="/models" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium text-[#FFD93D] hover:bg-[#FFD93D]/[0.06]">
            <Cpu className="w-4 h-4 shrink-0" /><span className="hidden lg:inline">150 Models</span>
          </Link>
          <Link to="/pricing" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium text-white/60 hover:text-white hover:bg-white/[0.04]">
            <DollarSign className="w-4 h-4 shrink-0" /><span className="hidden lg:inline">Pricing</span>
          </Link>
        </div>
      </nav>
      {user && (
        <div className="hidden lg:flex items-center gap-2 px-3 pb-4 text-white/60">
          {user.picture ? <img src={user.picture} alt="" className="w-6 h-6 rounded-full" /> : <div className="w-6 h-6 rounded-full bg-[#E040A0]/30" />}
          <div className="flex-1 min-w-0">
            <div className="text-[11px] text-white truncate">{user.name?.split(' ')[0]}</div>
            <div className="text-[9px] text-white/50">{formatCredits(user)} cr</div>
          </div>
        </div>
      )}
    </aside>
  );
}
